import { DiscoveryCycleCensusState, DiscoveryCycleCoverage } from "./Statistics/DiscoveryCycle";
import { MigrationType, MigrationTypeNouns } from "./Statistics/MigrationType";

/**
 * What the last scan concluded about a source link. There is deliberately no "not found" value:
 * `MigrationStatus.NotFound` on a file means something else — a file discovered from a correct link that then
 * could not be read at upload time.
 */
export enum SourceLinkOutcome {
  FilesDiscovered = "filesDiscovered",
  TargetEmpty = "targetEmpty",
  PartiallyDiscovered = "partiallyDiscovered",
  NothingDiscovered = "nothingDiscovered",
  Excluded = "excluded",
  DuplicateTarget = "duplicateTarget",
}

/**
 * The one set of names the six outcomes go by — on a link row, in a cycle's census and in the summary alike, so a
 * reader can tell that two figures under one name are the same count.
 */
export const SourceLinkOutcomeLabels: Record<SourceLinkOutcome, string> = {
  [SourceLinkOutcome.FilesDiscovered]: "Files found",
  [SourceLinkOutcome.TargetEmpty]: "Empty",
  [SourceLinkOutcome.PartiallyDiscovered]: "Partly found",
  [SourceLinkOutcome.NothingDiscovered]: "Nothing found",
  [SourceLinkOutcome.Excluded]: "Excluded",
  [SourceLinkOutcome.DuplicateTarget]: "Duplicate",
};

export const SourceLinkOutcomeVariants: Record<SourceLinkOutcome, string> = {
  [SourceLinkOutcome.FilesDiscovered]: "success",
  [SourceLinkOutcome.TargetEmpty]: "neutral",
  [SourceLinkOutcome.PartiallyDiscovered]: "warning",
  [SourceLinkOutcome.NothingDiscovered]: "danger",
  [SourceLinkOutcome.Excluded]: "neutral",
  [SourceLinkOutcome.DuplicateTarget]: "neutral",
};

export enum SourceLinkReason {
  Unknown = "unknown",
  PathMissing = "pathMissing",
  PathInvalid = "pathInvalid",
  AccessDenied = "accessDenied",
  PathTooLong = "pathTooLong",
  SourceUnreachable = "sourceUnreachable",
  IgnoreList = "ignoreList",
  FileTypeFilter = "fileTypeFilter",
  PathFileLimit = "pathFileLimit",
}

export const SourceLinkReasonLabels: Record<SourceLinkReason, string> = {
  [SourceLinkReason.Unknown]: "Unknown",
  [SourceLinkReason.PathMissing]: "Path missing",
  [SourceLinkReason.PathInvalid]: "Invalid path",
  [SourceLinkReason.AccessDenied]: "Access denied",
  [SourceLinkReason.PathTooLong]: "Path too long",
  [SourceLinkReason.SourceUnreachable]: "Source unreachable",
  [SourceLinkReason.IgnoreList]: "Ignore list",
  [SourceLinkReason.FileTypeFilter]: "File-type filter",
  [SourceLinkReason.PathFileLimit]: "Path file limit",
};

/**
 * Who can act on a link, derived server-side from its outcome and reason rather than stored. A path excluded by
 * configuration and a path nobody can read are both unresolved and have nothing else in common.
 */
export enum SourceLinkOwner {
  None = "none",
  FirmData = "firmData",
  Environment = "environment",
  Intended = "intended",
  TrackedElsewhere = "trackedElsewhere",
  DataQuality = "dataQuality",
}

export const SourceLinkOwnerLabels: Record<SourceLinkOwner, string> = {
  [SourceLinkOwner.None]: "Nothing to do",
  [SourceLinkOwner.FirmData]: "Firm data",
  [SourceLinkOwner.Environment]: "Environment",
  [SourceLinkOwner.Intended]: "Intended",
  [SourceLinkOwner.TrackedElsewhere]: "Tracked elsewhere",
  [SourceLinkOwner.DataQuality]: "Data quality",
};

/**
 * Where a link stands. Server-derived, and every row is in exactly one: an outstanding kind, healthy, or gone.
 *
 * The outstanding set is split by kind because its parts have nothing in common but being unresolved, and each
 * kind is both a summary figure and a filter option, so a figure and the rows behind it are the same set. Whether a
 * link has ever failed is not a state — a link that failed once and works now is healthy — and is filtered for
 * separately.
 */
export enum SourceLinkState {
  MissingOrUnreachable = "missingOrUnreachable",
  InvalidPath = "invalidPath",
  Duplicate = "duplicate",
  Excluded = "excluded",
  Healthy = "healthy",
  RemovedFromSource = "removedFromSource",
}

export const SourceLinkStateLabels: Record<SourceLinkState, string> = {
  [SourceLinkState.MissingOrUnreachable]: "Missing or unreachable",
  [SourceLinkState.InvalidPath]: "Invalid path",
  [SourceLinkState.Duplicate]: "Duplicate",
  [SourceLinkState.Excluded]: "Excluded",
  [SourceLinkState.Healthy]: "Healthy",
  [SourceLinkState.RemovedFromSource]: "Gone from source",
};

/**
 * The outstanding kinds, which the server counts exactly off the live index. Healthy, gone and "all" may reach into
 * the healthy set — tens of millions of rows for a large firm — so their counts are capped and the page never opens
 * on them.
 */
export const outstandingStates: readonly SourceLinkState[] = [
  SourceLinkState.MissingOrUnreachable,
  SourceLinkState.InvalidPath,
  SourceLinkState.Duplicate,
  SourceLinkState.Excluded,
];

export const isOutstanding = (state: SourceLinkState): boolean => outstandingStates.includes(state);

/** Why a link left the source. Set together with `removedFromSourceOn`. */
export enum SourceLinkRemovalReason {
  VanishedFromSource = "vanishedFromSource",
  EntityLeftScope = "entityLeftScope",
}

/** Why a link was cleared out, in the tab's own noun: "Case left discovery", not "Entity left discovery". */
export const removalReasonLabel = (reason: SourceLinkRemovalReason, migrationType: MigrationType): string => {
  if (reason === SourceLinkRemovalReason.VanishedFromSource) return "Removed at the source";
  const noun = MigrationTypeNouns[migrationType]?.one ?? "record";
  return `${noun.charAt(0).toUpperCase()}${noun.slice(1)} left discovery`;
};

export enum SourceLinkOrigin {
  SourceDatabase = "sourceDatabase",
  ConfiguredFolder = "configuredFolder",
  SourceApi = "sourceApi",
  BlobManifest = "blobManifest",
}

export const SourceLinkOriginLabels: Record<SourceLinkOrigin, string> = {
  [SourceLinkOrigin.SourceDatabase]: "Source database",
  [SourceLinkOrigin.ConfiguredFolder]: "Configured folder",
  [SourceLinkOrigin.SourceApi]: "Source API",
  [SourceLinkOrigin.BlobManifest]: "Blob manifest",
};

/** What the link points at. Unknown where the scan never got far enough to establish it. */
export enum SourceLinkTarget {
  Unknown = "unknown",
  File = "file",
  Folder = "folder",
}

// The API serialises with JsonIgnoreCondition.WhenWritingNull, so a null server-side field arrives as an absent
// property rather than an explicit null. Optional, not `| null`.
export interface SourceLink {
  id: string;
  entityType: MigrationType;
  migrationEntityId: string;
  entityLabel: string;
  linkPath: string;
  origin: SourceLinkOrigin;
  target: SourceLinkTarget;
  /** The physical table the link was read from. Absent for sources that offer no record identity. */
  sourceTable?: string;
  /** The TrialWorks tab the link was read from — the part somebody can navigate to, unlike `sourceTable`. */
  sourceTabLabel?: string;
  sourceRecordId?: string;
  /** The column the record id is a value of, so the three together name the source row outright. */
  sourceRecordColumn?: string;
  outcome: SourceLinkOutcome;
  /** Absent only on a link that has never failed. Retained after a link clears. */
  reason?: SourceLinkReason;
  owner: SourceLinkOwner;
  reasonDetail?: string;
  filesProducedCount: number;
  subPathFailureCount: number;
  lastSeenOn: string;
  /** Absent while the link is still outstanding. */
  resolvedOn?: string;
  /** Absent unless the link is gone from the source system entirely. */
  removedFromSourceOn?: string;
  /** Why it left, when it did. Absent while the source still offers the link. */
  removalReason?: SourceLinkRemovalReason;
  /** Derived server-side from the row; what the state filter selects on. */
  state: SourceLinkState;
  /**
   * On a duplicate, the source record whose link claimed the target — its own identity rather than a link id,
   * because a winning link succeeded and has no row here. Absent on every other outcome, and where the client
   * could not tell which link won.
   */
  supersededBySourceTable?: string;
  supersededBySourceRecordColumn?: string;
  supersededBySourceRecordId?: string;
  /** The discovery that produced the row, finer than `entityType`: case files and case folders are separate. */
  migrationType: MigrationType;
  /**
   * How many finished cycles of this link's own discovery that could have reached it have run since it or its
   * entity was last accounted for. Zero means the latest cycle accounted for it; absent means no finished cycle of
   * that type exists to measure against.
   *
   * Separates a link that is still broken from one nobody examines any more — both sit open, but a link whose
   * entity is still discovered stays at zero while a closed case's links climb. Saturates at
   * `CYCLES_SINCE_LAST_SEEN_CAP`.
   */
  cyclesSinceLastSeen?: number;
  /** What the previous scan concluded. A success here against a failing `outcome` is a regression. */
  previousOutcome?: SourceLinkOutcome;
}

/** One keyset page of links. `nextCursor` is opaque and goes back as `after`; absent on the last page. */
export interface SourceLinkPage {
  items: SourceLink[];
  nextCursor?: string;
  /** Exact for the exception views; the cap, meaning "at least this many", where `totalCountIsCapped`. */
  totalCount: number;
  totalCountIsCapped: boolean;
}

/** A file attributed to a link, as the drill-down shows it. */
export interface SourceLinkFile {
  id: string;
  migrationEntityId: string;
  filePath: string;
  filename?: string;
  uploadPathFromRoot?: string;
  status: string;
  fileSizeInMegabytes: number;
  fileModifiedOn?: string;
  lastMigratedOn?: string;
}

/** The latest finished cycle's link census for one discovery, as the summary tiles show it. */
export interface SourceLinkCycleCensus {
  cycleId: string;
  migrationType: MigrationType;
  startDate: string;
  endDate?: string;
  linksScanned?: number;
  linksFilesDiscovered?: number;
  linksTargetEmpty?: number;
  linksPartiallyDiscovered?: number;
  linksNothingDiscovered?: number;
  linksExcluded?: number;
  linksDuplicateTarget?: number;
  exceptionRowsReported: number;
  rootsUnreachable: number;
  degraded: boolean;
  /** How far the cycle's view of its scope can be trusted. See `DiscoveryCycleCoverage`. */
  coverage: DiscoveryCycleCoverage;
  /** The client ended the cycle before its reports landed; see `DiscoveryCycle.reportsPending`. */
  reportsPending: boolean;
  drainComplete?: boolean;
  /** Why the counters above are missing or partial, if they are. See `DiscoveryCycleCensusState`. */
  censusState: DiscoveryCycleCensusState;
  /**
   * The roots the cycle named as unreachable — what a bounded cycle steps around. Fewer than `rootsUnreachable`
   * where the client tallied roots it never named.
   */
  unreachableRoots: UnreachableRoot[];
}

/** A root a cycle could not read, and how many links it withheld beneath it as a result. */
export interface UnreachableRoot {
  pathRoot: string;
  linksSkipped: number;
}

/**
 * The outstanding links of one type as they stand now, one figure per outstanding state. A different series from a
 * cycle's census, which is what one cycle reported.
 */
export interface SourceLinkOutstanding {
  missingOrUnreachable: number;
  invalidPaths: number;
  duplicates: number;
  excluded: number;
  total: number;
}

/** The headline for one entity type: what the partial indexes and the census can say without counting the table. */
export interface SourceLinkSummary {
  entityType: MigrationType;
  outstanding: SourceLinkOutstanding;
  fullMapEntities: number;
  latestCycles: SourceLinkCycleCensus[];
  /**
   * When a cycle of this type last covered its scope, completely or around faults of known reach. Absent where
   * none ever has.
   */
  lastCoveredOn?: string;
  /**
   * The worst verdict among the latest cycles of the discoveries this type serves. Absent where no cycle has
   * finished. Only `unbounded` is worth alarming on.
   */
  coverage?: DiscoveryCycleCoverage;
}

/**
 * Mirrors `SourceLink.HasRegressed`, and must keep mirroring it: this decides the badge while
 * `SourceLinkRows.ApplyFilters` decides the "Broke since last cycle" filter, so a divergence shows an operator a
 * row wearing the badge that the filter then hides.
 *
 * `DuplicateTarget` is excluded for the reason the server gives: a link that now collides has lost nothing,
 * because the files still arrive by way of the link that claimed the target.
 */
export const hasRegressed = (link: SourceLink): boolean =>
  link.previousOutcome !== undefined &&
  isSuccessOutcome(link.previousOutcome) &&
  !isSuccessOutcome(link.outcome) &&
  link.outcome !== SourceLinkOutcome.DuplicateTarget;

/** Mirrors `SourceLink.IsSuccess`. */
export const isSuccessOutcome = (outcome: SourceLinkOutcome): boolean =>
  outcome === SourceLinkOutcome.FilesDiscovered || outcome === SourceLinkOutcome.TargetEmpty;

/** Mirrors `SourceLink.HasFailedBefore`: only a failure writes a reason, and a success never clears it. */
export const hasFailedBefore = (link: SourceLink): boolean => link.reason !== undefined;

/** Mirrors `GetSourceLinksQueryHandler.CyclesSinceLastSeenCap`: the server stops counting here. */
export const CYCLES_SINCE_LAST_SEEN_CAP = 100;

/** The age in cycles as the server counted it, with the cap read as "at least". */
export const formatCyclesSinceLastSeen = (cycles: number): string =>
  cycles >= CYCLES_SINCE_LAST_SEEN_CAP ? `${CYCLES_SINCE_LAST_SEEN_CAP}+` : `${cycles}`;
